import { useState, useEffect, useRef } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';

import { Dialog, DialogTitle, DialogActions, DialogContent, Typography, Button, Box } from '@mui/material';

import { queryState, selectResultState, parsedParamsState } from './States';
import { exeUpdateQuery } from '@/repositories/HttpRepository';
import LoadingButton from '@/components/Button/LoadingButton';

export default function UpdateConfirm() {
  const query = useRecoilValue(queryState);
  const [queryResult, setQueryResult] = useRecoilState(selectResultState);
  const parsedParams = useRecoilValue(parsedParamsState);

  const [updating, setUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmedQuery, setConfirmedQuery] = useState(null);
  const [confirmedParsedParams, setConfirmedParsedParams] = useState(null);

  const mountedRef = useRef(true);

  const isDialogOpen = !!queryResult?.execution;

  // Track mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Capture frozen snapshot when dialog opens
  useEffect(() => {
    if (isDialogOpen && confirmedQuery === null) {
      setConfirmedQuery(query.trim());
      setConfirmedParsedParams(parsedParams);
    }
  }, [isDialogOpen, query, parsedParams, confirmedQuery]);

  const cancelUpdate = () => {
    if (updating) {
      return;
    }
    setQueryResult({});
    setConfirmedQuery(null);
    setConfirmedParsedParams(null);
    setErrorMessage('');
  };

  const update = () => {
    if (confirmedQuery === null || updating) {
      return;
    }
    setUpdating(true);
    setErrorMessage('');
    exeUpdateQuery(confirmedQuery, confirmedParsedParams)
      .then((resp) => {
        if (!mountedRef.current) return;
        if (resp.ok) {
          setQueryResult(resp.data);
          setConfirmedQuery(null);
          setConfirmedParsedParams(null);
        } else {
          setErrorMessage(resp.data?.message || 'Update failed. Please try again.');
        }
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        setErrorMessage(error?.message || 'Network error. Please check your connection and try again.');
      })
      .finally(() => {
        if (!mountedRef.current) return;
        setUpdating(false);
      });
  };

  if (!isDialogOpen) {
    return null;
  }

  return (
    <Dialog fullWidth maxWidth="md" open onClose={() => {/* do nothing */}} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">
        <Typography variant="h4">Are you sure you want to run your update?</Typography>
      </DialogTitle>
      <DialogContent>
        Your query will update{' '}
        <span>
          <strong style={{ color: 'red' }}>{queryResult.execution.effectedRow}</strong>
        </span>{' '}
        records. Do you want to update?
        {errorMessage && (
          <Box mt={2} style={{ color: 'red' }}>
            {errorMessage}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button disableElevation onClick={cancelUpdate} variant="outlined" color="inherit" disabled={updating}>
          Cancel
        </Button>
        <LoadingButton color="primary" variant="contained" onClick={update} label="Update" loading={updating} />
      </DialogActions>
    </Dialog>
  );
}
